// src/components/audio/FlaggedRowNotesButton.js
//
// Notes button + popover for the ACTIONS column of flagged-rows tables.
// Lets a reviewer add and view notes for the audio attached to a flagged row
// without leaving the dashboard.
//
// SHIP 3 BEHAVIORAL CONTRACT
// ==========================
// - Button shows a count badge of existing notes for THIS audio file.
// - Click opens a popover anchored below the button:
//     - Header: filename + "Open in Audio Audit →" link
//     - List of existing notes for this file (compact, sorted by timestamp)
//       Each note can be deleted inline.
//     - Composer at bottom: textarea + tag dropdown + Save button
// - Notes from this surface use timestamp_seconds = 0 (no playback context;
//   the reviewer is noting the row, not a moment in the audio). They can
//   still seek to specific timestamps in Audio Audit afterwards.
// - Optimistic save: local state updates first, server save runs in background;
//   on failure, rollback + inline error.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { StickyNote, Plus, Trash2, X, Headphones, Clock, ChevronDown } from 'lucide-react';
import {
  TAGS, getTag, formatTimestamp,
  addNote, deleteNote, getNotesForPath, countNotesForPath,
} from './audioNotes';
import { DataService } from '../../services/dataService';

const FlaggedRowNotesButton = ({
  audioPath,
  audioColumn,
  filename,           // display-only
  submissionId,
  audioNotesDoc,
  onAudioNotesUpdated,
  projectId,
  userInfo,
  isDarkMode,
  onOpenAudit,        // optional: () => void to navigate to Audio Audit
}) => {
  const [open, setOpen]       = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [text, setText]       = useState('');
  const [tag, setTag]         = useState('probing');
  const [confirmDel, setConfirmDel] = useState(null);
  const wrapRef = useRef(null);
  const textareaRef = useRef(null);

  const notes = useMemo(
    () => getNotesForPath(audioNotesDoc, audioPath),
    [audioNotesDoc, audioPath]
  );
  const count = useMemo(
    () => countNotesForPath(audioNotesDoc, audioPath),
    [audioNotesDoc, audioPath]
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setConfirmDel(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus textarea on open
  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 50);
  }, [open]);

  if (!audioPath || !audioColumn || !projectId) return null;

  const colors = isDarkMode
    ? {
        fg: '#9CA3AF', bg: 'rgba(255,255,255,0.04)', bd: 'rgba(255,255,255,0.10)',
        fgHover: '#60A5FA', bgHover: 'rgba(96,165,250,0.10)',
        popBg: '#141516', popBd: 'rgba(255,255,255,0.10)',
        text: '#E7EAF0', textSec: '#B8BDC9', textMuted: '#6B7180',
        cellBg: 'rgba(255,255,255,0.025)', cellBd: 'rgba(255,255,255,0.06)',
        inputBg: 'rgba(0,0,0,0.2)',
        accent: '#38B6FF', accentDeep: '#1B6BE0',
        error: '#EF4444',
      }
    : {
        fg: '#6B7280', bg: 'rgba(0,0,0,0.04)', bd: 'rgba(0,0,0,0.10)',
        fgHover: '#1B6BE0', bgHover: 'rgba(27,107,224,0.08)',
        popBg: '#FFFFFF', popBd: 'rgba(0,0,0,0.10)',
        text: '#15181D', textSec: '#3A3F4A', textMuted: '#6B7180',
        cellBg: 'rgba(0,0,0,0.02)', cellBd: 'rgba(0,0,0,0.07)',
        inputBg: '#FFFFFF',
        accent: '#1B6BE0', accentDeep: '#1453B0',
        error: '#DC2626',
      };

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;

    const author = userInfo?.name || userInfo?.email || 'reviewer';
    const optimistic = addNote(audioNotesDoc || { notes: [], dispositions: {} }, {
      audio_path:        audioPath,
      audio_column:      audioColumn,
      submission_id:     submissionId,
      timestamp_seconds: 0,
      text:              trimmed,
      tag,
      author,
    });

    if (onAudioNotesUpdated) onAudioNotesUpdated(optimistic);
    setError(null);
    setSaving(true);

    try {
      await DataService.saveAudioNotes(projectId, optimistic);
      setText('');
      // Keep tag selection — reviewers often log multiple notes of the same kind
    } catch (err) {
      console.error('[FlaggedRowNotesButton] save failed:', err);
      if (onAudioNotesUpdated) onAudioNotesUpdated(audioNotesDoc);
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (saving) return;
    const optimistic = deleteNote(audioNotesDoc, noteId);
    if (onAudioNotesUpdated) onAudioNotesUpdated(optimistic);
    setError(null);
    setSaving(true);
    setConfirmDel(null);
    try {
      await DataService.saveAudioNotes(projectId, optimistic);
    } catch (err) {
      console.error('[FlaggedRowNotesButton] delete failed:', err);
      if (onAudioNotesUpdated) onAudioNotesUpdated(audioNotesDoc);
      setError(err?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      {/* Trigger button with count badge */}
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title={count > 0 ? `${count} note${count === 1 ? '' : 's'} for this audio` : 'Add a note'}
        style={{
          width: '24px', height: '22px',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          gap: '3px',
          padding: '0 4px',
          background: open ? colors.bgHover : (count > 0 ? colors.bgHover : colors.bg),
          color: open || count > 0 ? colors.fgHover : colors.fg,
          border: `1px solid ${colors.bd}`,
          borderRadius: '5px',
          cursor: 'pointer',
          fontFamily: "'Outfit', system-ui, sans-serif",
          fontSize: '10px',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          transition: 'all 0.12s ease',
        }}
        onMouseEnter={(e) => {
          if (open) return;
          e.currentTarget.style.background = colors.bgHover;
          e.currentTarget.style.color = colors.fgHover;
        }}
        onMouseLeave={(e) => {
          if (open) return;
          e.currentTarget.style.background = count > 0 ? colors.bgHover : colors.bg;
          e.currentTarget.style.color = count > 0 ? colors.fgHover : colors.fg;
        }}
      >
        <StickyNote size={11} />
        {count > 0 && <span>{count}</span>}
      </button>

      {/* Popover */}
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)', right: 0,
            width: '340px',
            background: colors.popBg,
            border: `1px solid ${colors.popBd}`,
            borderRadius: '10px',
            boxShadow: isDarkMode
              ? '0 8px 28px rgba(0,0,0,0.55)'
              : '0 8px 28px rgba(0,0,0,0.14)',
            zIndex: 200,
            fontFamily: "'Outfit', system-ui, sans-serif",
            overflow: 'visible',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 12px',
            borderBottom: `1px solid ${colors.cellBd}`,
            background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
          }}>
            <StickyNote size={12} style={{ color: colors.fgHover, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '10px', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.1em',
                color: colors.textMuted,
              }}>
                Notes ({count})
              </div>
              <div title={filename || audioPath} style={{
                fontSize: '11px', color: colors.textSec,
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                marginTop: '1px',
              }}>
                {basename(filename || audioPath)}
              </div>
            </div>
            {onOpenAudit && (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenAudit(); setOpen(false); }}
                title="Open in Audio Audit"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '4px 8px',
                  background: 'transparent',
                  color: colors.accent,
                  border: `1px solid ${colors.cellBd}`,
                  borderRadius: '5px',
                  fontSize: '10.5px',
                  fontFamily: 'inherit',
                  fontWeight: 500,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <Headphones size={10} />
                Audit
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              title="Close"
              style={{
                width: '22px', height: '22px',
                background: 'transparent',
                border: 'none',
                color: colors.textMuted,
                cursor: 'pointer',
                borderRadius: '4px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <X size={13} />
            </button>
          </div>

          {/* Existing notes */}
          {notes.length > 0 && (
            <div style={{
              maxHeight: '220px',
              overflowY: 'auto',
              padding: '8px 12px',
              display: 'flex', flexDirection: 'column', gap: '6px',
              borderBottom: `1px solid ${colors.cellBd}`,
            }}>
              {notes.map((n) => {
                const t = getTag(n.tag);
                const confirming = confirmDel === n.id;
                return (
                  <div key={n.id} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '6px',
                    padding: '6px 8px',
                    background: colors.cellBg,
                    border: `1px solid ${colors.cellBd}`,
                    borderRadius: '6px',
                  }}>
                    {n.timestamp_seconds > 0 && (
                      <span title={`At ${formatTimestamp(n.timestamp_seconds)}`} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                        fontSize: '10px',
                        color: colors.accent,
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontVariantNumeric: 'tabular-nums',
                        flexShrink: 0,
                        marginTop: '1px',
                      }}>
                        <Clock size={9} />
                        {formatTimestamp(n.timestamp_seconds)}
                      </span>
                    )}
                    <span style={{
                      flexShrink: 0,
                      padding: '1px 6px',
                      background: `${t.color}1A`,
                      color: t.color,
                      borderRadius: '999px',
                      fontSize: '9px',
                      fontWeight: 600,
                      textTransform: 'lowercase',
                      letterSpacing: '0.02em',
                      marginTop: '1px',
                    }}>
                      {t.label}
                    </span>
                    <div style={{
                      flex: 1, minWidth: 0,
                      fontSize: '12px',
                      color: colors.text,
                      lineHeight: 1.45,
                      wordBreak: 'break-word',
                    }}>
                      {n.text}
                    </div>
                    {!confirming ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDel(n.id); }}
                        title="Delete"
                        style={{
                          width: '20px', height: '20px',
                          background: 'transparent',
                          border: 'none',
                          color: colors.textMuted,
                          cursor: 'pointer',
                          borderRadius: '4px',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          padding: 0,
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${colors.error}1A`;
                          e.currentTarget.style.color = colors.error;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = colors.textMuted;
                        }}
                      >
                        <Trash2 size={10} />
                      </button>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(n.id); }}
                          style={{
                            padding: '2px 6px',
                            fontSize: '10px',
                            color: '#FFFFFF',
                            background: colors.error,
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontWeight: 600,
                          }}
                        >
                          Delete
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDel(null); }}
                          style={{
                            padding: '2px 6px',
                            fontSize: '10px',
                            color: colors.textSec,
                            background: 'transparent',
                            border: `1px solid ${colors.cellBd}`,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          Cancel
                        </button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Composer */}
          <div style={{ padding: '10px 12px 12px' }}>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKey}
              placeholder="What did you notice? (Ctrl+Enter to save)"
              rows={2}
              style={{
                width: '100%',
                padding: '7px 9px',
                background: colors.inputBg,
                border: `1px solid ${colors.cellBd}`,
                borderRadius: '6px',
                color: colors.text,
                fontFamily: 'inherit',
                fontSize: '12.5px',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                lineHeight: 1.5,
              }}
            />
            <div style={{
              display: 'flex', gap: '6px', alignItems: 'center',
              marginTop: '8px',
            }}>
              <TagPicker
                value={tag}
                onChange={setTag}
                colors={colors}
                isDarkMode={isDarkMode}
              />
              <span style={{ flex: 1 }} />
              <button
                onClick={(e) => { e.stopPropagation(); handleSave(); }}
                disabled={!text.trim() || saving}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '5px 11px',
                  background: text.trim() && !saving
                    ? `linear-gradient(135deg, ${colors.accentDeep}, ${colors.accent})`
                    : (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
                  color: text.trim() && !saving ? '#FFFFFF' : colors.textMuted,
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'inherit',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  cursor: text.trim() && !saving ? 'pointer' : 'default',
                  boxShadow: text.trim() && !saving ? `0 1px 4px ${colors.accent}33` : 'none',
                }}
              >
                <Plus size={11} />
                {saving ? 'Saving…' : 'Add note'}
              </button>
            </div>
            {error && (
              <div style={{
                marginTop: '8px',
                padding: '5px 8px',
                fontSize: '11px',
                color: colors.error,
                background: `${colors.error}12`,
                border: `1px solid ${colors.error}33`,
                borderRadius: '5px',
              }}>
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </span>
  );
};

// ─── Themed tag picker (replaces native <select> which Chromium-on-Windows
// renders with un-themable OS chrome). Opens upward so it stays inside the
// parent popover. Uses the same TAGS vocabulary as audioNotes.js. ─────────
const TagPicker = ({ value, onChange, colors, isDarkMode }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = getTag(value);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title="Tag"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 8px',
          background: open ? colors.bgHover : colors.inputBg,
          border: `1px solid ${colors.cellBd}`,
          borderRadius: '5px',
          color: colors.text,
          fontFamily: 'inherit',
          fontSize: '11.5px',
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        <span style={{
          width: '6px', height: '6px',
          borderRadius: '50%',
          background: current.color,
          flexShrink: 0,
        }} />
        {current.label}
        <ChevronDown size={11} style={{ opacity: 0.55, marginLeft: '2px' }} />
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 4px)', left: 0,
            minWidth: '160px',
            background: colors.popBg,
            border: `1px solid ${colors.popBd}`,
            borderRadius: '8px',
            boxShadow: isDarkMode
              ? '0 6px 22px rgba(0,0,0,0.55)'
              : '0 6px 22px rgba(0,0,0,0.14)',
            padding: '4px',
            zIndex: 10,
            display: 'flex', flexDirection: 'column', gap: '1px',
          }}
        >
          {TAGS.map(t => {
            const active = t.id === value;
            return (
              <button
                key={t.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(t.id);
                  setOpen(false);
                }}
                title={t.description}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%',
                  padding: '6px 8px',
                  background: active ? colors.bgHover : 'transparent',
                  border: 'none',
                  borderRadius: '5px',
                  color: colors.text,
                  fontFamily: 'inherit',
                  fontSize: '12px',
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.1s ease',
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = colors.bgHover;
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{
                  width: '6px', height: '6px',
                  borderRadius: '50%',
                  background: t.color,
                  flexShrink: 0,
                }} />
                {t.label}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
};

const basename = (p) => {
  if (!p) return '';
  const m = String(p).match(/[^\\/]+$/);
  return m ? m[0] : String(p);
};

export default FlaggedRowNotesButton;